import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import ee from '../../../services/earth-engine';
import { Box, Button, Paper, Typography } from '@material-ui/core';
import { useTheme } from '@material-ui/core/styles';
import GoogleMap from '../map/GoogleMap';
import ShapeEditControl from '../map/ShapeEditControl';
import { useShapeVertexEditing } from '../../../hooks/useShapeVertexEditing';
import { useErrorNotice } from '../../../hooks/useErrorNotice';
import { clearAcquisitionImageCache } from '../../../hooks/useStepNavigations';
import StepperButtons from './StepperButtons';
import * as Map from '../../../common/map';
import { aoiGeometry, isMultiPolygon, parseKmlArea } from '../../../common/aoi';
import { Actions as Acquisition } from '../../../store/ducks/acquisition';
import TourGuider from '../tour/TourGuider';
import { useIsMounted, useLocalStorage } from '../../../common/utils';
import HelpButton from '../core/HelpButton';
import ActivityIndicator from '../core/ActivityIndicator.jsx';
import { get as getSatellite } from '../../../common/satellites';
import { calculateCloudCoverage as SentinelCloudCoverageCache } from '../../../algorithms/satellite/sentinel';
import { calculateCloudCoverage } from '../../../algorithms/satellite/landsatToa';
import { eeEval } from '../../../algorithms/utils/index.js';

/**
 * @function AOIChooser
 * @description This is the page of the second step of shoreline acquisition.
 * It allows the user to draw the Area of Interest (AOI) and to save it in the storage
 * @param {Object} navigate - props provided by the render parameter of React Router contain match, location and history
 * *Called by:
 * @see {AcquisitionPage} - shoreline and bathymetry's acquisition
 * @returns {JSX.Element} - renders Map, drawing tools along header and footer
 */
const AOIChooser = ({ navigate, sessionImportButton }) => {
  const dispatch = useDispatch();
  /**
   * Problems are reported in the corner instead of in a blocking dialog - see {@link useErrorNotice}.
   */
  const notifyError = useErrorNotice();
  const [t] = useTranslation();
  const theme = useTheme();
  const savedCoordinates = useSelector((state) => state.acquisition.coordinates);
  const savedKmlFileName = useSelector((state) => state.acquisition.kmlFileName);
  const [overlay, setOverlay] = useState(null);
  const [coordinates, setCoordinates] = useState(() => savedCoordinates ?? null);
  const [isDataTranningSelected, setIsDataTranningSelected] = useState(false);
  const [tranningData, setTranningData] = useState([]);
  const [message, setMessage] = useState('');
  const [kmlFileName, setKmlFileName] = useState(() => savedKmlFileName ?? '');
  const [kmlLoading, setKmlLoading] = useState(false);
  const [aoiLoading, setAoiLoading] = useState(false);
  const isMounted = useIsMounted();
  const aoiEditing = useShapeVertexEditing({ onChange: setCoordinates });
  let url = window.location.href;
  const isBathymetry = url.indexOf('bathymetry') !== -1;

  /**
   * @function saveZoomChange
   * @description Saves current map zoom to session storage.
   * @returns {void}
   */
  const saveZoomChange = () => {
    let zoom = Map.getZoomLevel();
    if (zoom) window.sessionStorage.setItem('zoom_level', zoom);
  };

  useEffect(() => {
    window.sessionStorage.removeItem('zoom_level');
    Map.setDrawingControlsVisible(true);
    const intervalId = setInterval(saveZoomChange, 5000);
    return () => {
      clearInterval(intervalId);
      Map.onZoomChange(() => saveZoomChange());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * @function handleDrawing
   * @description handle drawing the AOI on the map and disable the drawing controls when the AOI is drawn.
   * @param {google.maps.Polygon|google.maps.Rectangle} overlay - The drawn shape's overlay.
   * @param {Array<Array<Number>>} coordinates - The drawn shape's coordinates, as [lng, lat] pairs.
   * @param {('polygon'|'rectangle')} [type] - The drawn shape's type.
   * @returns {void}
   */
  const handleDrawing = (overlay, coordinates, type) => {
    setOverlay(overlay);
    setCoordinates(coordinates);
    aoiEditing.setShape(overlay, type);
    Map.setDrawingControlsVisible(false);
  };

  /**
   * @function handleMapLoad
   * @description Redraws the previously-committed AOI polygon once the (freshly mounted) map
   * instance is ready, when the user navigates back to this step after already having chosen
   * an AOI in a previous visit (the raw `overlay` object from Redux is tied to the old, now
   * unmounted map and can't be reused directly).
   * @returns {void}
   */
  const handleMapLoad = () => {
    if (!coordinates) return;
    const openRing = Map.toOpenRing(coordinates);
    const overlayFixed = Map.drawOutline(openRing);
    setOverlay(overlayFixed);
    setCoordinates(openRing);
    aoiEditing.setShape(isMultiPolygon(openRing) ? null : overlayFixed, 'polygon');
    Map.setDrawingControlsVisible(false);
    Map.fitToCoordinates(openRing);
  };

  /**
   * @function handleUndo
   * @description handle the undoing of the AOI and enable the drawing controls
   * @returns {void}
   */
  const handleUndo = () => {
    aoiEditing.setShape(null);
    overlay.setMap(null);
    setOverlay(null);
    setCoordinates(null);
    Map.setDrawingControlsVisible(true);
  };

  /**
   * @function saveCloudCoverageValues
   * @description Calculates cloud coverage percentages for satellite images within a
   * specified region of interest (ROI) and date range. The results are cached in
   * session storage as a date-to-cloud-coverage mapping for quick retrieval.
   * @param {Object} satellite - Satellite object containing missions array with collection names
   * @param {String} startDate  - date to start getting cloud coverage
   * @param {Object} roi - google map earth polygon
   * *Called by:
   * @see {handleChoose} - Function called after completing the AOI Chooser step
   * @returns {void}
   */
  const saveCloudCoverageValues = async function (satellite, startDate, roi) {
    const cloudPropertyName = 'roiCloudPercentage';
    const dtPropertyName = 'system:time_start';
    let collectionWithROICloud =
      satellite.name === 'Landsat'
        ? calculateCloudCoverage(satellite, roi, startDate)
        : SentinelCloudCoverageCache(roi);
    let imagesWithCloud = await eeEval(collectionWithROICloud);
    let dateCloudMap = imagesWithCloud.features.map(function (el) {
      return {
        dt: el.properties[dtPropertyName],
        cloud: el.properties[cloudPropertyName],
      };
    });
    window.sessionStorage.setItem('roiCloudCoveragePerDate', JSON.stringify(dateCloudMap));
  };

  /**
   * @function getSelectedSatellite
   * @description Get chosen satellite on the previous acquisition step
   * @returns {Object} - Information about the current chosen satellite
   */
  const getSelectedSatellite = function () {
    let selectedSatelliteIndex = Number.parseInt(window.sessionStorage.getItem('selectedSatelliteIndex'));
    let satellite = getSatellite(selectedSatelliteIndex);
    return satellite;
  };

  /**
   * @function handleChoose
   * @description Processes the user-selected AOI by calculating cloud coverage for the
   * entire mission period of the selected satellite, then dispatches the AOI data to
   * the application state. If the user lacks GEE API access, redirects to the signup page.
   * @see {@link saveCloudCoverageValues} - calculates cloud coverage
   * @see {@link Acquisition.setAOI} - adds AOI data to the application state
   * @returns {void}
   */
  const handleChoose = () => {
    try {
      aoiEditing.stopEditing();
      let satellite = getSelectedSatellite();
      let startYear = satellite.missions[0].startYear;
      saveCloudCoverageValues(satellite, `${startYear}-01-01`, aoiGeometry(coordinates));
      if (JSON.stringify(coordinates) !== JSON.stringify(savedCoordinates)) {
        clearAcquisitionImageCache();
        dispatch(Acquisition.setAvailableDates([]));
        dispatch(Acquisition.setPeriod(null, null));
      }
      try {
        dispatch(Acquisition.setAOI(overlay, coordinates, aoiGeometry(coordinates), kmlFileName));
      } catch (e) {
        notifyError(
          'Your account still is not allowed to use GEE API. You should complete your register using this link: https://earthengine.google.com/signup/',
        );
        window.location = 'https://earthengine.google.com/signup/';
      }
    } finally {
      if (isMounted.current) setAoiLoading(false);
    }
  };

  const drawn = Boolean(coordinates);

  const readFileTrainingData = function () {
    let fileField = document.getElementById('file_import_training_data');
    if (fileField.files.length === 0) {
      notifyError(t('forms.core.fileErrors.selectFileFirst'));
      return false;
    }
    const file = fileField.files[0];
    const reader = new FileReader();
    reader.addEventListener(
      'load',
      function () {
        const fileContent = reader.result;
        let lines = fileContent.split('\n');
        lines = lines.splice(1, lines.length);
        let matrix = [];
        lines.forEach((line) => {
          let columns = line.split(',');
          if (columns[0] !== '' && columns[0] !== null && !Number.isNaN(Number.parseFloat(columns[0]))) {
            let x = Number.parseFloat(columns[0]);
            let y = Number.parseFloat(columns[1]);
            let z = Number.parseFloat(columns[2]);
            matrix.push([x, y, z]);
          }
        });
        setIsDataTranningSelected(true);
        setTranningData(matrix);
        let fileName = fileField.value;
        let posUnterscore = fileName.lastIndexOf('_');
        let posDot = fileName.lastIndexOf('.');
        if (posDot - posUnterscore >= 6) {
          let day = fileName.substring(posUnterscore + 1, posUnterscore + 3);
          let month = fileName.substring(posUnterscore + 3, posUnterscore + 5);
          let year = fileName.substring(posUnterscore + 5, posUnterscore + 9);
          document.getElementById('date_traning_file').value = year + '-' + month + '-' + day;
        }
      },
      false,
    );
    if (file) reader.readAsText(file);
  };

  const readFileData = function () {
    let fileField = document.getElementById('file_import_kml');
    if (fileField.files.length === 0) {
      notifyError(t('forms.core.fileErrors.selectFileFirst'));
      return false;
    }
    const file = fileField.files[0];
    setKmlFileName(file.name);
    setKmlLoading(true);
    const reader = new FileReader();
    reader.addEventListener(
      'load',
      function () {
        try {
          const fileContent = reader.result;
          let isKml = fileContent.indexOf('<coordinates>') !== -1;
          if (isKml) {
            const area = parseKmlArea(fileContent);
            if (area) {
              const openRing = Map.toOpenRing(area);
              let overlayFixed = Map.drawOutline(openRing);
              setOverlay(overlayFixed);
              setCoordinates(openRing);
              aoiEditing.setShape(isMultiPolygon(openRing) ? null : overlayFixed, 'polygon');
              Map.fitToCoordinates(openRing);
              Map.setDrawingControlsVisible(false);
              closeFilePanel();
              setMessage('KML file processed.');
            } else {
              notifyError(t('forms.core.fileErrors.kmlNoCoordinates'));
            }
          } else {
            notifyError(t('forms.core.fileErrors.unreadableFile'));
          }
        } finally {
          setKmlLoading(false);
        }
      },
      false,
    );
    if (file) {
      reader.readAsText(file);
    } else {
      setKmlLoading(false);
    }
  };

  const handleDisplayImportKML = function () {
    let dialog = document.getElementById('dialog_kml');
    dialog.showModal();
  };

  const handleDisplayImportTrainingData = function () {
    let dialog = document.getElementById('dialog_training_csv');
    dialog.showModal();
    let coordinates_system = document.getElementById('coordinates_system');
    if (coordinates_system.value === '') {
      coordinates_system.value = 'EPSG:4326';
      window.sessionStorage.setItem('coordinatesSystem', 'EPSG:4326');
    }
  };

  const closeFilePanel = function () {
    let dialog = document.getElementById('dialog_kml');
    dialog.close();
  };

  const closeTrainingFilePanel = function () {
    let dialog = document.getElementById('dialog_training_csv');
    dialog.close();
  };

  const confirmTrainingFilePanel = function () {
    let dateTranning = document.getElementById('date_traning_file').value;
    let coordinatesSystem = document.getElementById('coordinates_system').value;
    if (coordinatesSystem === '') {
      notifyError(t('forms.core.fileErrors.missingCoordinateSystem'));
      return;
    }
    if (!isDataTranningSelected) {
      notifyError(t('forms.core.fileErrors.missingTrainingFile'));
    } else if (dateTranning === '') {
      notifyError(t('forms.core.fileErrors.missingDate'));
    } else {
      let matrix = tranningData;
      if (coordinatesSystem.indexOf('4326') === -1) {
        let points = tranningData.map((point) => {
          return ee.Feature(ee.Geometry.Point([point[0], point[1]], coordinatesSystem));
        });
        var utm2latlon = points.map(function (feature) {
          return feature.transform('EPSG:4326');
        });
        let fc = ee.FeatureCollection(utm2latlon);
        let coordinatesConverted = fc.getInfo();
        matrix = [];
        coordinatesConverted.features.forEach((el) => {
          matrix.push(el.geometry.coordinates);
        });
      }
      Acquisition.setTranningData(tranningData);
      Acquisition.setTranningDate(dateTranning);
      setMessage('Trainning file processed.');
      closeTrainingFilePanel();
      plotTranningData(matrix);
    }
  };

  const plotTranningData = function (matrix) {
    let table = [];
    matrix.forEach((row) => table.push([row[0], row[1]]));
    let samples = table;
    let coordinatesArray = samples;
    Map.drawCircle(coordinatesArray);
    Map.centralize(coordinatesArray[0][1], coordinatesArray[0][0]);
  };

  const displayTrainingButton = () => {
    if (isBathymetry) return 'inline-block';
    return 'none';
  };

  const showHelpCoordinatesSystem = () => {
    document.getElementById('help_coordinate_system').showModal();
  };

  const showHelpDateTranning = () => {
    document.getElementById('help_date_tranning').showModal();
  };

  const showHelpTranningFile = () => {
    document.getElementById('help_tranning_file').showModal();
  };

  const steps = [
    {
      selector: '#areachooser',
      content: t('tour.acquisition.2.info'),
    },
    {
      selector: '#importkml',
      content: t('tour.csqueeze.2.importKml'),
    },
  ];
  isBathymetry &&
    steps.push({
      selector: '#datatraining',
      content: t('tour.acquisition.2.dataTraining'),
    });
  const [isTourOpen, setIsTourOpen] = useLocalStorage('showROITour', true);

  useEffect(() => {
    document.body.style.overflow = isTourOpen ? 'hidden' : 'auto';
  }, [isTourOpen]);

  return (
    <Box>
      <div
        align="center"
        style={{
          color: 'darkgreen',
          fontSize: '14px',
          padding: message !== '' ? '10px' : '0px',
          display: message !== '' ? 'none' : 'block',
        }}>
        {message}
      </div>
      <dialog id="dialog_kml">
        <Paper variant="outlined" id="import_kml_component" style={{ padding: 16, borderRadius: 10 }}>
          <Typography variant="subtitle2" style={{ fontWeight: 600, marginBottom: 10 }}>
            {t('forms.acquisition.2.importLegend')}
          </Typography>
          <Button variant="outlined" color="primary" component="label" size="small">
            {t('forms.acquisition.2.importTitle')}
            <input
              hidden
              id="file_import_kml"
              accept=".kml"
              type="file"
              onClick={(e) => {
                e.target.value = null;
              }}
              onChange={readFileData}
            />
          </Button>
          <Typography variant="caption" display="block" style={{ marginTop: 8 }}>
            {kmlFileName || t('forms.acquisition.2.noKmlFileSelected')}
          </Typography>
        </Paper>
        <br />
        <Button color="secondary" variant="contained" onClick={closeFilePanel}>
          {t('forms.map.cancel')}
        </Button>
      </dialog>
      <dialog id="dialog_training_csv">
        <fieldset style={{ borderColor: '#BBB', display: 'block' }} id="import_csv_component">
          <legend>{t('forms.acquisition.2.dataTrainningLegend')}</legend>
          <svg
            onClick={showHelpTranningFile}
            style={{ fontSize: '24' }}
            className="MuiSvgIcon-root MuiSvgIcon-colorPrimary MuiSvgIcon-fontSizeInherit"
            focusable="false"
            viewBox="0 0 24 24"
            aria-hidden="true">
            <path d="M11 18h2v-2h-2v2zm1-16C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm0-14c-2.21 0-4 1.79-4 4h2c0-1.1.9-2 2-2s2 .9 2 2c0 2-3 1.75-3 5h2c0-2.25 3-2.5 3-5 0-2.21-1.79-4-4-4z"></path>
          </svg>
          &nbsp;
          <input id="file_import_training_data" accept=".csv" type="file" onChange={readFileTrainingData} />
          <p>{t('forms.acquisition.2.dataTrainningFileLabel')}</p>
          <svg
            onClick={showHelpDateTranning}
            style={{ fontSize: '24' }}
            className="MuiSvgIcon-root MuiSvgIcon-colorPrimary MuiSvgIcon-fontSizeInherit"
            focusable="false"
            viewBox="0 0 24 24"
            aria-hidden="true">
            <path d="M11 18h2v-2h-2v2zm1-16C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm0-14c-2.21 0-4 1.79-4 4h2c0-1.1.9-2 2-2s2 .9 2 2c0 2-3 1.75-3 5h2c0-2.25 3-2.5 3-5 0-2.21-1.79-4-4-4z"></path>
          </svg>
          &nbsp;
          <input type="date" id="date_traning_file" />
          <br />
          <br />
          Coordinates system*:
          <br />
          <span onClick={showHelpCoordinatesSystem}>
            <svg
              style={{ fontSize: '24' }}
              className="MuiSvgIcon-root MuiSvgIcon-colorPrimary MuiSvgIcon-fontSizeInherit"
              focusable="false"
              viewBox="0 0 24 24"
              aria-hidden="true">
              <path d="M11 18h2v-2h-2v2zm1-16C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm0-14c-2.21 0-4 1.79-4 4h2c0-1.1.9-2 2-2s2 .9 2 2c0 2-3 1.75-3 5h2c0-2.25 3-2.5 3-5 0-2.21-1.79-4-4-4z"></path>
            </svg>
          </span>
          &nbsp;
          <select style={{ display: 'none' }}>
            <option>EPSG:32722</option>
          </select>
          <input
            list="coordinates_list"
            id="coordinates_system"
            onInput={() => {
              window.sessionStorage.setItem('coordinatesSystem', document.getElementById('coordinates_system').value);
            }}
          />{' '}
          (example type "EPSG:4326".)
          <div>
            Find coordinates system values here:{' '}
            <a href="https://epsg.io" target="blank">
              https://epsg.io
            </a>
          </div>
          <datalist id="coordinates_list">
            <option value="EPSG:32722"></option>
            <option value="EPSG:4326"></option>
          </datalist>
          <br />
        </fieldset>
        <br />
        <Button color="secondary" variant="contained" onClick={closeTrainingFilePanel}>
          {t('forms.map.cancel')}
        </Button>
        &nbsp;&nbsp;
        <Button color="primary" variant="contained" onClick={confirmTrainingFilePanel}>
          Confirm
        </Button>
      </dialog>

      <dialog
        id="help_coordinate_system"
        style={{
          border: '3px solid blue',
          borderRadius: '10px',
          width: '400px',
          maxWidth: '400px',
        }}>
        *{t('forms.acquisition.2.helpCoordinateSystem')}
        <div style={{ fontWidth: '9px', color: '#555', paddingTop: '20px' }}>Press [ESC] to close.</div>
      </dialog>

      <dialog
        id="help_date_tranning"
        style={{
          border: '3px solid blue',
          borderRadius: '10px',
          width: '400px',
          maxWidth: '400px',
        }}>
        *{t('forms.acquisition.2.dataTrainningDateHint')}
        <div style={{ fontWidth: '9px', color: '#555', paddingTop: '20px' }}>Press [ESC] to close.</div>
      </dialog>

      <dialog
        id="help_tranning_file"
        style={{
          border: '3px solid blue',
          borderRadius: '10px',
          width: '400px',
          maxWidth: '400px',
        }}>
        Input File Format:
        <br />
        Format: Comma-Separated Values (.csv)
        <br />
        Columns: 3<br />
        Order: X, Y, Z<br />
        Decimal: Period (.)
        <br />
        <div style={{ fontWidth: '9px', color: '#555', paddingTop: '20px' }}>Press [ESC] to close.</div>
      </dialog>

      <div
        style={{
          display: 'flex',
          flexDirection: 'row-reverse',
          justifyContent: 'space-around',
        }}>
        <HelpButton
          onClickFunction={() => {
            setIsTourOpen(true);
          }}
          extra={sessionImportButton}
        />
        <div id={'datatraining'} style={{ alignContent: 'center' }}>
          <Button
            onClick={handleDisplayImportTrainingData}
            style={{
              cursor: 'pointer',
              display: displayTrainingButton(),
              color: '#26a69a',
              left: 0,
              top: 0,
              backgroundColor: theme.palette.background.paper,
              zIndex: 99,
            }}>
            {t('forms.acquisition.2.dataTrainning')}
          </Button>
        </div>
        <div id={'importkml'} style={{ alignContent: 'center' }}>
          <Button
            onClick={handleDisplayImportKML}
            style={{
              cursor: 'pointer',
              color: '#26a69a',
              backgroundColor: theme.palette.background.paper,
              zIndex: 99,
            }}>
            {t('forms.acquisition.2.importTitle')}
          </Button>
        </div>
      </div>
      {isBathymetry && (
        <div align="center" style={{ padding: '2%', marginTop: '0' }}>
          <div>
            {t('forms.acquisition.2.example')}:&nbsp;
            <br />
            Babitonga: <a href={'/babitonga.kml'.toString()}>Area of interesst (.kml)</a>
            &nbsp;&nbsp;|&nbsp;&nbsp;
            <a href={'/batimetria_1K_20052018.csv'.toString()}>Trainning data file (.csv) - EPSG:32722</a>
            <br />
            Babitonga: <a href={'/babitonga.kml'.toString()}>Area of interesst (.kml)</a>
            &nbsp;&nbsp;|&nbsp;&nbsp;
            <a href={'/batimetria_1K_ESPG4326positivo_20052018.csv'.toString()}>
              Trainning data file (.csv) - EPSG:4326
            </a>
          </div>
        </div>
      )}
      <div id={'areachooser'}>
        <GoogleMap style={{ width: '100%', height: '75vh' }} onRegionDrawn={handleDrawing} onLoad={handleMapLoad} />
      </div>
      <ShapeEditControl editing={aoiEditing} visible={drawn} />
      <StepperButtons
        navigate={navigate}
        nextDisabled={aoiEditing.geometryError !== '' || (isBathymetry ? !isDataTranningSelected || !drawn : !drawn)}
        onNext={handleChoose}>
        <Button onClick={handleUndo} disabled={drawn === false} color="secondary" variant="outlined">
          {t('forms.acquisition.2.undo')}
        </Button>
      </StepperButtons>
      <TourGuider steps={steps} isOpen={isTourOpen} setIsTourOpen={setIsTourOpen} />
      {(kmlLoading || aoiLoading) && (
        <ActivityIndicator
          forceShow={true}
          title={kmlLoading ? t('forms.core.loading.kml') : t('forms.core.loading.cloudCoverage')}
        />
      )}
    </Box>
  );
};

export default AOIChooser;
